<template>
  <component
    :is="componentTag"
    v-bind="componentProps"
    :class="buttonClassList"
    :aria-busy="loading || undefined"
    :aria-disabled="isInteractive ? undefined : String(disabled || loading)"
    @click="handleClick"
  >
    <InlineSpinner
      v-if="loading"
      class="app-button__spinner"
      :size="18"
      :thickness="2"
    />
    <template v-else>
      <slot>{{ label }}</slot>
    </template>
  </component>
</template>

<script>
import { defineComponent } from 'vue';
import InlineSpinner from '@/components/loader/InlineSpinner.vue';

export default defineComponent({
  name: 'AppButton',
  inheritAttrs: false,
  components: {
    InlineSpinner,
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    to: {
      type: [String, Object],
      default: null
    },
    href: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: 'button'
    },
    variant: {
      type: String,
      default: 'primary'
    },
    block: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  computed: {
    componentTag() {
      if (this.to) {
        return 'router-link';
      }
      if (this.href) {
        return 'a';
      }
      return 'button';
    },
    isInteractive() {
      return this.componentTag === 'button';
    },
    buttonClassList() {
      const classes = ['app-button'];
      const variantClass = `app-button--${this.variant}`;
      classes.push(variantClass);
      if (this.block) {
        classes.push('app-button--block');
      }
      if (this.loading) {
        classes.push('app-button--loading');
      }

      const attrClass = this.$attrs.class;
      if (!attrClass) {
        return classes;
      }
      if (Array.isArray(attrClass)) {
        return classes.concat(attrClass);
      }
      return classes.concat(attrClass);
    },
    componentProps() {
      const base = { ...this.$attrs };
      delete base.class;
      if (this.componentTag === 'router-link') {
        base.to = this.to;
      } else if (this.componentTag === 'a') {
        base.href = this.href;
        if (!base.target) {
          base.target = '_self';
        }
      } else {
        base.type = this.type || 'button';
        base.disabled = this.disabled || this.loading;
      }

      if (this.disabled || this.loading) {
        if (this.componentTag !== 'button') {
          base.tabindex = -1;
          base['aria-disabled'] = 'true';
        }
      }

      return base;
    }
  },
  methods: {
    handleClick(event) {
      if (this.disabled || this.loading) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
        event.stopPropagation();
        return;
      }
      this.$emit('click', event);
    }
  }
});
</script>

<style scoped>
.app-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  min-height: 4.2rem;
  padding: 0.8rem 1.6rem;
  border-radius: 999px;
  border: 1px solid #000;
  background: #fff;
  color: #000;
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.app-button--primary {
  background: #000;
  color: #fff;
}

.app-button--secondary {
  background: #fff;
  color: #000;
  border-color: #E5E7EB;
}

.app-button--brand {
  background: #5B6FEE;
  border-color: #5B6FEE;
  color: #fff;
  box-shadow: 0 8px 20px rgba(91, 111, 238, 0.28);
}

.app-button--danger {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
  box-shadow: 0 12px 26px rgba(220, 38, 38, 0.25);
}

.app-button--outline {
  background: transparent;
  color: #000;
  border-color: #E5E7EB;
}

.app-button--block {
  width: 100%;
}

.app-button:focus-visible {
  outline: 2px solid rgba(70, 70, 70, 0.6);
  outline-offset: 3px;
}

.app-button--primary:not(:disabled):not(.app-button--loading):hover {
  background: #2f2f2f;
  color: #fff;
  border-color: #2f2f2f;
  box-shadow: 0 10px 26px rgba(17, 24, 39, 0.16);
}

.app-button--brand:not(:disabled):not(.app-button--loading):hover {
  background: #4558e0;
  border-color: #4558e0;
  color: #fff;
  box-shadow: 0 12px 28px rgba(91, 111, 238, 0.38);
}

.app-button--danger:not(:disabled):not(.app-button--loading):hover {
  background: #b91c1c;
  color: #fff;
  border-color: #b91c1c;
  box-shadow: 0 14px 30px rgba(185, 28, 28, 0.32);
}

.app-button--secondary:not(:disabled):not(.app-button--loading):hover,
.app-button--outline:not(:disabled):not(.app-button--loading):hover {
  background: #f2f3f5;
  color: #111827;
  border-color: #d1d5db;
  box-shadow: 0 8px 20px rgba(17, 24, 39, 0.08);
}

.app-button:disabled,
.app-button[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.6;
  box-shadow: none;
}

.app-button__spinner {
  flex-shrink: 0;
}
</style>
